import { type OrderItem, type OrderItem as OrderItemType } from '@hooks-api/types/dataModel/fpOrder'
import { DELIVERY_CLASS } from '@hooks-api/types/dataModel/shipping'
import { type WpSubshipSetting } from '@hooks-api/types/dataModel/wp'
import { Box } from '@pets-ui/Box/Box'
import { Chip } from '@pets-ui/Chip/Chip'
import { Divider } from '@pets-ui/Divider/Divider'
import { HStack } from '@pets-ui/HStack/HStack'
import { Image } from '@pets-ui/Image/Image'
import { Link } from '@pets-ui/Link/Link'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { formatPrice } from '@utils'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import ProductAlt from '../../../assets/image/product-alt.svg?react'
import { useContextOrderDetail } from '../OrderDetailProvider/OrderDetailProvider'

const styles = stylex.create({
  productName: {
    flex: '1',
  },
  chip: {
    marginRight: '8px',
  },
  specificAddonOrderItemsContainer: {
    padding: '12px',
    borderColor: colors.grey300,
    borderStyle: 'solid',
    borderWidth: '1px 0 0 0',
    backgroundColor: colors.grey50,
  },
})

interface ShipmentOrderItemLayoutProps {
  orderItem: OrderItemType
  subshipType?: WpSubshipSetting['type']
  isOfficialWebsite?: boolean
  specificAddonOrderItems?: OrderItem[]
}

export function ShipmentOrderItem({ orderItem, subshipType, isOfficialWebsite = false, specificAddonOrderItems }: ShipmentOrderItemLayoutProps) {
  const { t } = useTranslation()
  const { currency, hasAddonV2, b2bProductImageFirst } = useContextOrderDetail()
  const { thumbnail_url, last_gallery_image, product_name, quantity, unit_price, subtotal, total, is_addon, is_giveaway, is_addon_v2, is_amount_giveaway, is_from_point_exchange, delivery_class, credit_card_affiliate_code, order_item_type } = orderItem
  const productImageSrc = b2bProductImageFirst ? last_gallery_image || thumbnail_url : thumbnail_url
  const discount = total - subtotal
  const isFree = is_giveaway || is_amount_giveaway
  const isDeliverySubship = delivery_class === DELIVERY_CLASS.SUBSHIP
  const isStaggeredShipping = isDeliverySubship && subshipType === 'staggered_shipping'
  const isSubscription = isDeliverySubship && subshipType === 'subscription'
  const isGiveaway = credit_card_affiliate_code ? false : is_giveaway
  const isSinopacDawho = credit_card_affiliate_code === 'sinopacDawho'
  const { products } = useContextOrderDetail()
  const product = products?.find(product => product.id === String(orderItem.product_id))
  const productDisplay = useMemo(() => (
    <HStack gap="8px">
      <Image src={productImageSrc} alt={product_name} width={60} height={60} fallback={<ProductAlt width={60} height={60} />} />
      <Text component="span" rootStyle={styles.productName}>
        {isSubscription && <Chip color="red" size="small" variant="square" rootStyle={styles.chip}>{t('orderDetail.subscription', { defaultValue: '訂閱' })}</Chip>}
        {isStaggeredShipping && <Chip color="red" size="small" variant="square" rootStyle={styles.chip}>{t('orderDetail.warehouse', { defaultValue: '寄倉' })}</Chip>}
        {isGiveaway && <Chip color="red" size="small" variant="square" rootStyle={styles.chip}>{t('orderDetail.giveaway', { defaultValue: '贈品' })}</Chip>}
        {is_from_point_exchange && <Chip color="red" size="small" variant="square" rootStyle={styles.chip}>{t('orderDetail.pointExchange', { defaultValue: '點數兌換券' })}</Chip>}
        {is_addon && !hasAddonV2 && <Chip color="red" size="small" variant="square" rootStyle={styles.chip}>{t('orderDetail.addon', { defaultValue: '加價購' })}</Chip>}
        {is_addon_v2 && <Chip color="red" size="small" variant="square" rootStyle={styles.chip}>{t('orderDetail.addon', { defaultValue: '加價購' })}</Chip>}
        {isSinopacDawho && <Chip color="red" size="small" variant="square" rootStyle={styles.chip}>{t('orderDetail.SinopacDawhoGiveaway', { defaultValue: '永豐大戶贈品' })}</Chip>}
        {product_name}
      </Text>
    </HStack>
  ), [productImageSrc, product_name, isSubscription, t, isStaggeredShipping, isGiveaway, is_from_point_exchange, is_addon, hasAddonV2, is_addon_v2, isSinopacDawho])

  return (
    <VStack borderWidth={1} borderColor={colors.grey300} borderStyle="solid">
      <Box>
        <HStack p={2} gap={2} borderColor={colors.grey300} borderWidth={1} backgroundColor={colors.white}>
          {product?.product_page_url
            ? (
              <Link href={product.product_page_url} target="_self" underline={false} color="inherit">
                {productDisplay}
              </Link>
            )
            : (
              <>{ productDisplay }</>
            )}
        </HStack>
        <Divider />
        <HStack gap={2} borderColor={colors.grey300} borderWidth={1} borderTopWidth={0} justifyContent="space-between" backgroundColor={colors.white}>
          <VStack gap={1} p={2} minWidth={72} backgroundColor={colors.grey100}>
            <Text color={colors.grey600}>{t('orderDetail.unitPrice', { defaultValue: '單價' })}</Text>
            <Text color={colors.grey600}>{t('orderDetail.quantity', { defaultValue: '數量' })}</Text>
            <Text color={colors.grey600}>{t('orderDetail.subtotal', { defaultValue: '小計' })}</Text>
          </VStack>
          <VStack gap={1} p={2}>
            <Text align="right">{isFree ? t('orderDetail.free', { defaultValue: '免費' }) : formatPrice(currency, unit_price)}</Text>
            <Text align="right">{quantity}</Text>
            <HStack gap={1} justifyContent="flex-end">
              {
                is_addon_v2
                  ? (
                    <>
                      <Text>{!isOfficialWebsite && t('orderDetail.afterDiscount', { defaultValue: '折扣後' })}</Text>
                      <Text align="right">{isFree ? t('orderDetail.free', { defaultValue: '免費' }) : formatPrice(currency, total)}</Text>
                    </>
                  )
                  : <Text align="right">{isFree ? t('orderDetail.free', { defaultValue: '免費' }) : formatPrice(currency, subtotal)}</Text>
              }
              {discount > 0 && (
                <Text color={colors.grey500}>
                  {t('orderDetail.discountValue', { value: formatPrice(currency, discount), defaultValue: formatPrice(currency, discount, '($value折扣)') })}
                </Text>
              )}
            </HStack>
          </VStack>
        </HStack>
      </Box>
      {order_item_type === 'product' && specificAddonOrderItems?.map((specificAddonItem: OrderItem) => (
        <Box key={specificAddonItem.id} rootStyle={styles.specificAddonOrderItemsContainer}>
          <ShipmentOrderItem
            orderItem={specificAddonItem}
            subshipType={subshipType}
            isOfficialWebsite={isOfficialWebsite}
          />
        </Box>
      ))}
    </VStack>
  )
}
